import { PDFDocument } from "@pdfme/pdf-lib";

/**
 * Merges pages from a source PDF into the target PDF document.
 * Uses ignoreEncryption option to handle PDFs that may have encryption flags
 * but don't actually require a password.
 */
export async function mergePdfPages(
  targetPdf: PDFDocument,
  sourcePdfBytes: ArrayBuffer,
): Promise<void> {
  // Load with ignoreEncryption to handle PDFs that have encryption metadata
  // but don't actually require a password (common with some PDF generators)
  const sourcePdf = await PDFDocument.load(sourcePdfBytes, {
    ignoreEncryption: true,
  });
  const copiedPages = await targetPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());

  copiedPages.forEach((page) => targetPdf.addPage(page));
}

/**
 * Draws an image to a new page in the PDF document.
 * Scales images down if they exceed A4 dimensions.
 *
 * Note: pdf-lib has limited PNG support - it may fail on PNGs with:
 * - Interlacing (Adam7)
 * - Certain color types
 * - Some compression methods
 *
 * If PNG embedding fails, this function will throw an error that should be caught
 * by the caller.
 */
export async function drawImageToPdf(
  targetPdf: PDFDocument,
  contentType: string | undefined,
  fileBytes: ArrayBuffer,
): Promise<void> {
  let image;

  if (contentType === "image/png") {
    // Note: embedPng may fail on interlaced PNGs or those with unsupported features
    image = await targetPdf.embedPng(fileBytes);
  } else {
    // Handle JPEG and JPG
    image = await targetPdf.embedJpg(fileBytes);
  }

  // Create a page with the image dimensions (or scale down if too large)
  const maxWidth = 595; // A4 width in points
  const maxHeight = 842; // A4 height in points
  let width = image.width;
  let height = image.height;

  // Ensure we have valid dimensions
  if (!width || !height || width <= 0 || height <= 0) {
    throw new Error(`Invalid image dimensions: ${width}x${height}`);
  }

  // Scale down if image is larger than A4
  if (width > maxWidth || height > maxHeight) {
    const widthRatio = maxWidth / width;
    const heightRatio = maxHeight / height;
    const ratio = Math.min(widthRatio, heightRatio);
    width = width * ratio;
    height = height * ratio;
  }

  const page = targetPdf.addPage([width, height]);
  page.drawImage(image, {
    x: 0,
    y: 0,
    width,
    height,
  });
}

/**
 * Downloads and triggers a file save dialog for the merged PDF
 */
export function downloadPdf(pdfBytes: Uint8Array, filename: string): void {
  const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
